import type { I18n, MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import type { StepUpProof } from '@/shared/action';
import type { Cents } from '@/shared/format/money';

// "Adicionar Lançamento" (`BankAccountView/Drawer/AddAccountEntry/*`, `BankAccountViewContainer.tsx:105-163`):
// os tipos do select, a validação do yup do legado e o corpo do `POST BankAccount/{id}/Entry`.

/* eslint-disable lingui/no-unlocalized-strings -- valores do fio e rótulos que o legado manda no corpo (`*Display`) */
/** Os 15 tipos, na ordem do select (`AddAccountEntryForm.tsx:35-51`), com o rótulo que o legado grava em `accountEntryTypeDisplay`. */
export const ACCOUNT_ENTRY_TYPES = [
  ['PixSent', 'Pix Enviado'],
  ['PixReservalSent', 'Estorno de Pix Enviado'],
  ['PixRefundSent', 'Devolução de Pix Enviado'],
  ['PixReceived', 'Pix Recebido'],
  ['PixRefundReceived', 'Devolução de Pix Recebido'],
  ['PixReversalRefundReceived', 'Estorno de Devolução do Pix Recebido'],
  ['BlockBalance', 'Bloqueio de Saldo'],
  ['EntryDebit', 'Débito Simples'],
  ['EntryCredit', 'Crédito Simples'],
  ['DepositByBankSlip', 'Depósito por Boleto'],
  ['BillingFee', 'Tarifa de Processamento'],
  ['PaymentBankSlip', 'Pagamento de Boleto'],
  ['TransferDebit', 'Debito de Transferencia'],
  ['AdministrativeExpenses', 'Despesas operacionais'],
  ['PixFare', 'Tarifa Pix'],
] as const;
/* eslint-enable lingui/no-unlocalized-strings */

export type AccountEntryType = (typeof ACCOUNT_ENTRY_TYPES)[number][0];
export const ACCOUNT_ENTRY_TYPE_IDS = ACCOUNT_ENTRY_TYPES.map(([value]) => value) as unknown as readonly [AccountEntryType, ...AccountEntryType[]];

/** Rótulos da tela, traduzidos. "Debito de Transferencia" ganha os acentos (U20-5); o fio leva o do legado. */
const ENTRY_TYPE_LABELS: Record<AccountEntryType, MessageDescriptor> = {
  PixSent: msg`Pix Enviado`,
  PixReservalSent: msg`Estorno de Pix Enviado`,
  PixRefundSent: msg`Devolução de Pix Enviado`,
  PixReceived: msg`Pix Recebido`,
  PixRefundReceived: msg`Devolução de Pix Recebido`,
  PixReversalRefundReceived: msg`Estorno de Devolução do Pix Recebido`,
  BlockBalance: msg`Bloqueio de Saldo`,
  EntryDebit: msg`Débito Simples`,
  EntryCredit: msg`Crédito Simples`,
  DepositByBankSlip: msg`Depósito por Boleto`,
  BillingFee: msg`Tarifa de Processamento`,
  PaymentBankSlip: msg`Pagamento de Boleto`,
  TransferDebit: msg`Débito de Transferência`,
  AdministrativeExpenses: msg`Despesas operacionais`,
  PixFare: msg`Tarifa Pix`,
};

export function accountEntryTypeOptions(i18n: I18n) {
  return ACCOUNT_ENTRY_TYPES.map(([value]) => ({ value, label: i18n._(ENTRY_TYPE_LABELS[value]) }));
}

export function accountEntryTypeLabel(i18n: I18n, type: string | null | undefined): string {
  const found = ACCOUNT_ENTRY_TYPES.find(([value]) => value === type);
  return found ? i18n._(ENTRY_TYPE_LABELS[found[0]]) : '';
}

/** O formulário, com os padrões do legado (`AddAccountEntrySchema.ts:23-28`): valor 0, o resto `null`. */
export interface AddEntryValues {
  valueInCents: Cents | null;
  commentsDoc: string | null;
  complement: string | null;
  accountEntryType: string | null;
}

export const ADD_ENTRY_EMPTY: AddEntryValues = { valueInCents: 0, commentsDoc: null, complement: null, accountEntryType: null };

/**
 * `validationSchemaAddAccountEntryForm` (`AddAccountEntrySchema.ts:4-21`, yup 0.32): `null` cai no
 * `typeError`, o vazio no `required`; as mensagens com os espaços do legado ("Documento :", "Complemento  ").
 */
export function addEntrySchema(i18n: I18n): z.ZodType<AddEntryValues, AddEntryValues> {
  return z.object({
    valueInCents: z
      .number()
      .nullable()
      .superRefine((value, ctx) => {
        if (value === null) ctx.addIssue({ code: 'custom', message: i18n._(msg`Valor precisa ser um número.`) });
        else if (value <= 0) ctx.addIssue({ code: 'custom', message: i18n._(msg`Valor: Precisa ser positivo.`) });
      }),
    commentsDoc: z
      .string()
      .nullable()
      .superRefine((value, ctx) => {
        if (value === null) ctx.addIssue({ code: 'custom', message: i18n._(msg`Documento inválido.`) });
        else if (value === '') ctx.addIssue({ code: 'custom', message: i18n._(msg`Documento : Precisa ser preenchido.`) });
        else if (value.length > 20) ctx.addIssue({ code: 'custom', message: i18n._(msg`Documento: Máximo de 20 caracteres.`) });
      }),
    complement: z
      .string()
      .nullable()
      .superRefine((value, ctx) => {
        if (value === null) ctx.addIssue({ code: 'custom', message: i18n._(msg`Complemento  inválido.`) });
        else if (value === '') ctx.addIssue({ code: 'custom', message: i18n._(msg`Complemento  : Precisa ser preenchido.`) });
        else if (value.length > 25) ctx.addIssue({ code: 'custom', message: i18n._(msg`Complemento: Máximo de 25 caracteres.`) });
      }),
    accountEntryType: z
      .string()
      .nullable()
      .superRefine((value, ctx) => {
        if (value === null) ctx.addIssue({ code: 'custom', message: i18n._(msg`Tipo de lançamento inválido.`) });
        else if (value === '') ctx.addIssue({ code: 'custom', message: i18n._(msg`Selecione o tipo de lançamento.`) });
      }),
  });
}

/** Corpo do `POST BankAccount/{id}/Entry`, na ordem do legado: o formulário inteiro, o `*Display` do select e a prova. */
export interface AccountEntryBody {
  readonly valueInCents: Cents;
  readonly commentsDoc: string;
  readonly complement: string;
  readonly accountEntryType: string;
  readonly accountEntryTypeDisplay: string;
  readonly sessionId: string;
  readonly code: string;
}

/**
 * `{ ...formFieldValues, sessionId, code }` (`BankAccountViewContainer.tsx:117-121`). O `SelectFormField`
 * do legado grava também `accountEntryTypeDisplay` com o rótulo em pt-BR (`SelectFormField.tsx:116-117`).
 * Só roda depois do schema, então nenhum campo é `null`.
 */
export function accountEntryBody(values: AddEntryValues, proof: StepUpProof): AccountEntryBody {
  const type = values.accountEntryType ?? '';
  return {
    valueInCents: values.valueInCents ?? 0,
    commentsDoc: values.commentsDoc ?? '',
    complement: values.complement ?? '',
    accountEntryType: type,
    accountEntryTypeDisplay: ACCOUNT_ENTRY_TYPES.find(([value]) => value === type)?.[1] ?? '',
    sessionId: proof.sessionId,
    code: proof.code,
  };
}

/** O rascunho que o agente deixa no formulário (U24: só prepara). O valor chega em reais (`'1234.56'`) e vira centavos. */
export const addEntryDraftInput = z.strictObject({
  amount: z
    .string()
    .regex(/^\d{1,13}(\.\d{1,2})?$/)
    .optional(),
  document: z.string().min(1).max(20).optional(),
  complement: z.string().min(1).max(25).optional(),
  entryType: z.enum(ACCOUNT_ENTRY_TYPE_IDS).optional(),
});
export type AddEntryDraftInput = z.infer<typeof addEntryDraftInput>;
